import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"

type AuditRow = {
  id: number
  token: string
  show_title: string | null
  input_url: string
  email: string
  grade: string | null
  status: string
  apple_state: string | null
  kit_synced: boolean
  internal: boolean
  created_at: string
}

export default function AdminPodcastAuditsIndex({ audits }: { audits: AuditRow[] }) {
  return (
    <>
      <Head title="Podcast Audits">
        <meta name="description" content="Podcast discoverability audit leads captured by the free audit tool." />
        <meta property="og:title" content="Podcast Audits" />
        <meta property="og:description" content="Podcast discoverability audit leads captured by the free audit tool." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>Podcast Audits</h1>
          <p className="mt-1">
            {audits.length} {audits.length === 1 ? "audit" : "audits"} run via the
            free podcast discoverability audit tool. Each one is a lead.
          </p>
        </div>

        {audits.length === 0 ? (
          <p className="mt-10 text-ink-muted">No audits yet.</p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-md border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3 font-medium text-ink-muted">Show</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Email</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Grade</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Apple</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Kit</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Ran</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {audits.map((a) => (
                  <tr key={a.id} className="hover:bg-surface">
                    <td className="px-4 py-3">
                      <Link
                        href={`/admin/podcast_audits/${a.id}`}
                        className="font-medium text-ink-display no-underline hover:text-accent-display"
                      >
                        {a.show_title || a.input_url}
                      </Link>
                      {a.show_title && <span className="block text-xs text-ink-muted">{a.input_url}</span>}
                    </td>
                    <td className="px-4 py-3">
                      {/* Research runs aren't leads — label them rather than
                          showing a mailto to an address nobody reads. */}
                      {a.internal ? (
                        <Badge tone="muted">Research</Badge>
                      ) : (
                        <a href={`mailto:${a.email}`} className="text-ink-body no-underline hover:text-accent-display">
                          {a.email}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {a.grade ? <GradePill grade={a.grade} /> : <StatusBadge status={a.status} />}
                    </td>
                    <td className="px-4 py-3">
                      <ApplePill state={a.apple_state} />
                    </td>
                    <td className="px-4 py-3">
                      {a.kit_synced ? <Badge tone="signal">Synced</Badge> : <Badge tone="muted">—</Badge>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink-muted">{formatDate(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </>
  )
}

// Pass is green, middling is amber, failing is red — matching the findings on
// the report itself, so a grade means the same thing in both places.
function GradePill({ grade }: { grade: string }) {
  const tone = grade === "A" || grade === "B" ? "success" : grade === "C" ? "signal" : "danger"
  return <Badge tone={tone}>{grade}</Badge>
}

function StatusBadge({ status }: { status: string }) {
  if (status === "failed") return <Badge tone="danger">Failed</Badge>
  return <Badge tone="muted">{status === "running" ? "Running" : "Pending"}</Badge>
}

function ApplePill({ state }: { state: string | null }) {
  if (!state) return <span className="text-ink-muted">—</span>
  if (state === "found" || state === "duplicate") return <Badge tone="success">{state}</Badge>
  // Apple can't see the show at all — the worst outcome, and the hottest lead.
  if (state === "not_indexed") return <Badge tone="danger">Not indexed</Badge>
  return <Badge tone="muted">{state}</Badge>
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
